import React, {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import srtParser2 from 'srt-parser-2';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {CONTENT_STUDIO_PRICING_SOURCES} from '@/lib/analytics/analyticsConstants';
import {SESSION_ID_NOT_FOUND} from '@/lib/delta/deltaConstants.js';
import {
  handleContentStudioError,
  raiseContentStudioError,
} from '@/utils/contentStudioErrorHandling';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import RephraseContent from '@/views/content-studio/components/rephrasing-page/RephraseContent';
import RephraseDisclaimer from '@/views/content-studio/components/rephrasing-page/RephraseDisclaimer';
import {
  calculateVideoTokensForAssets,
  mapAssetToSelectedAsset,
} from '@/views/content-studio/components/utils';
import {FEATURE_ID} from '@/views/content-studio/constants';
import useOpenPricing, {
  OPEN_PRICING_TYPE,
} from '@/views/content-studio/hooks/use-open-pricing';
import resolveCreditAccessAction, {
  CREDIT_ACCESS_ACTION,
} from '@/views/content-studio/utils/resolveCreditAccessAction';
import Conditional from '@/components/conditional';
import useApplyRephrase from '@/hooks/content-studio/mutations/use-apply-rephrase';
import useInitializeRephrasingProject from '@/hooks/content-studio/mutations/use-initialize-rephrasing-project';
import useAssetQuery from '@/hooks/content-studio/use-asset-query';
import useNavigation from '@/hooks/use-navigation';
import contentStudioStore, {
  contentStudioActions,
  ErrorKind,
  SelectedAsset,
} from '@/stores/contentStudioStore';
import ErrorToast from '../error-toast/ErrorToast';
import FeaturePage from '../feature-page/FeaturePage';
import RephraseEditor from './RephraseEditor';
import RephraseVideoDisplay from './RephraseVideoDisplay';

function RephrasingPage() {
  const {navigate} = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitializingProject, setIsInitializingProject] = useState(false);
  const assetId = searchParams.get('assetId');
  const {mutate: initializeRephraseProject} = useInitializeRephrasingProject();
  const {mutate: applyRephraseMutation} = useApplyRephrase();
  const openPricing = useOpenPricing();
  const {selectedAssets, currentRephraseProject, files} = contentStudioStore(
    useShallow((state) => ({
      selectedAssets: state.selectedAssets,
      currentRephraseProject: state.currentRephraseProject,
      files: state.files,
    }))
  );

  const {data: assetData} = useAssetQuery({
    id: assetId || selectedAssets?.[0]?.id || '',
    enabled: !selectedAssets?.[0],
  });

  const selectedAsset = useMemo(() => {
    if (selectedAssets?.[0]) {
      return selectedAssets[0];
    }
    if (assetData) {
      return mapAssetToSelectedAsset(assetData);
    }

    return null;
  }, [assetData, selectedAssets]);

  useEffect(() => {
    if (!assetId && selectedAsset) {
      searchParams.set('assetId', selectedAsset.id);
      setSearchParams(searchParams);
    }
  }, [assetId, selectedAsset]);

  useEffect(() => {
    if (assetData) {
      contentStudioActions.setSelectedAssets([
        mapAssetToSelectedAsset(assetData),
      ]);
    }
  }, [assetData]);

  const getRequiredCredits = () => {
    if (selectedAssets.length === 0 && !selectedAsset) {
      return 0;
    }
    return calculateVideoTokensForAssets(
      selectedAssets.length ? selectedAssets : [selectedAsset as SelectedAsset]
    );
  };

  const checkAndShowPricingIfNeeded = async () => {
    const requiredCredits = getRequiredCredits();
    const creditAccessAction = await resolveCreditAccessAction(requiredCredits);

    if (creditAccessAction !== CREDIT_ACCESS_ACTION.NO_OP) {
      const openType =
        creditAccessAction === CREDIT_ACCESS_ACTION.REDIRECT_TO_PRICING
          ? OPEN_PRICING_TYPE.REDIRECT
          : OPEN_PRICING_TYPE.MODAL;

      openPricing(openType, CONTENT_STUDIO_PRICING_SOURCES.EDITOR_GENERATE, {
        onSuccessCallback: () => {
          initializeProject(true);
        },
      });
      return true;
    }
    return false;
  };

  const initializeProject = async (skipCreditCheck = false) => {
    if (!currentRephraseProject && !assetId && selectedAssets.length !== 1) {
      raiseContentStudioError('No asset selected', ErrorKind.FAILURE);
      navigate(ROUTES.CONTENT_LAB_REPHRASE_ASSETS);
    }

    const assetIdToUse = assetId || selectedAssets[0]?.id;

    if (!skipCreditCheck && (await checkAndShowPricingIfNeeded())) {
      return;
    }

    if (assetIdToUse) {
      setIsInitializingProject(true);
      const interactionId = uuid();
      const sessionId = AnalyticsService.getSessionId() || SESSION_ID_NOT_FOUND;
      initializeRephraseProject(
        {
          assetId: assetIdToUse,
          projectName: 'Rephrase',
          analytics_params: {
            interaction_id: interactionId,
            session_id: sessionId,
          },
        },
        {
          onError: (error) => {
            setIsInitializingProject(false);
            handleContentStudioError(
              error,
              ErrorKind.FAILURE,
              'Failed to create Rescript project'
            );
          },
          onSuccess: () => {
            setIsInitializingProject(false);
          },
        }
      );
    }
  };

  useEffect(() => {
    contentStudioActions.clearCurrentRephraseProject();
  }, []);

  const [currentIndex, setCurrentIndex] = useState<string | null>(null);
  const applyRephrase = () => {
    if (!currentRephraseProject) {
      raiseContentStudioError('No Rescript project found', ErrorKind.FAILURE);
      return;
    }

    setIsSubmitting(true);
    // eslint-disable-next-line new-cap
    const srtParser = new srtParser2();
    const parsedSrt = srtParser.toSrt(
      currentRephraseProject.currentSrt.map((item) => ({
        ...item,
        text: item.currentText,
      }))
    );
    const encoder = new TextEncoder();
    const uint8Array = encoder.encode(parsedSrt);
    const base64Srt = btoa(String.fromCharCode(...uint8Array));

    if (base64Srt) {
      applyRephraseMutation(
        {
          projectId: currentRephraseProject.id,
          base64Srt,
        },
        {
          onSuccess: () => {
            setIsSubmitting(false);
            contentStudioActions.clearCurrentRephraseProject();
            contentStudioActions.setSelectedAssets([]);
            contentStudioActions.setIsProjectShelfOpen(true);
            navigate(ROUTES.CONTENT_LAB);
          },
          onError: () => {
            setIsSubmitting(false);
            raiseContentStudioError(
              'Failed to apply rescript',
              ErrorKind.FAILURE
            );
          },
        }
      );
    } else {
      setIsSubmitting(false);
    }
  };

  const generateVideoFileUrl = () => {
    const file = files[selectedAsset?.fileName || ''];
    if (file) {
      return URL.createObjectURL(file.file);
    }
    return '';
  };

  const videoProps = {
    videoName:
      currentRephraseProject?.videoName || selectedAsset?.fileName || '',
    videoUrl:
      currentRephraseProject?.videoUrl ||
      selectedAsset?.url ||
      generateVideoFileUrl(),
    subtitleTime: currentRephraseProject?.currentSrt
      ? (currentRephraseProject?.currentSrt.find(
          (item) => item.id === currentIndex
        )?.startSeconds ?? 0)
      : 0,
  };

  const isUploading = selectedAsset?.isUploading || false;

  return (
    <FeaturePage featureId={FEATURE_ID.REPHRASE}>
      <Conditional
        condition={!!currentRephraseProject}
        fallback={
          <RephraseDisclaimer
            requiredCredits={getRequiredCredits()}
            actionEnabled={!isUploading}
            onSubmit={() => initializeProject()}
            isLoading={isInitializingProject || isUploading}
            buttonTooltip={
              isUploading
                ? translate('files-uploading-tooltip', {
                    featureName: 'Rephrase',
                  })
                : ''
            }
          />
        }
      >
        <RephraseContent>
          <RephraseEditor
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
            applyRephrase={applyRephrase}
            isSubmitting={isSubmitting}
          />
        </RephraseContent>
      </Conditional>

      <RephraseVideoDisplay {...videoProps} />
      <ErrorToast />
    </FeaturePage>
  );
}

export default RephrasingPage;
